import debug from 'debug';
import { denormalize, normalize } from 'normalizr';
import { RSAA } from 'redux-api-middleware';
import formatISO from 'date-fns/formatISO';
import parseISO from 'date-fns/parseISO';
import isAfter from 'date-fns/isAfter';
import has from 'lodash/has';
import get from 'lodash/get';
import { linearChannels as linearChannelsSchemas } from 'app-webtech-core/schemas';
import { selectors } from 'app-webtech-core/selectors';
import { FETCH_ALL } from './types';
import { linearChannelsPropertiesSelector } from '../selectors/properties';
import prependApiBaseUrl from '../../../helpers/prependApiBaseUrl';
import { refreshToken } from '../../application/actions';

const log = debug('linearChannels:actions:fetchAll');

const {
  LinearChannelNumberSchema,
  LinearChannelSchema,
  LinearChannelSlugSchema,
  CategoryListSchema,
} = linearChannelsSchemas;

const defaultFilter = (item) => has(item, 'stitched.urls[0].url');

const defaultSort = (items) =>
  items.sort((a, b) => (parseInt(a.number, 10) < parseInt(b.number, 10) ? -1 : 1));

const filterData = ({ data, customFilter }) => data.filter(customFilter);

const addUrlForNonStitchedChannels = (data, state) => {
  const dataCopy = Array.isArray(data) ? [...data] : [...data.channels];

  let sessionUrl = null;

  const promises = dataCopy.map(async (item) => {
    let res = item;
    if (!item.isStitched) {
      try {
        if (
          get(item, 'timelines.length') === 0 ||
          get(item, 'timelines[0].episode._id') === undefined
        ) {
          throw new Error(' bad timelines len / episode id');
        }
        const episodeId = item.timelines[0].episode._id;
        const response = await fetch(
          prependApiBaseUrl(`/v2/episodes/${episodeId}/clips.json`, null, state),
        );
        if (!response.ok) {
          throw new Error('response !ok');
        }
        const json = await response.json();
        if (
          get(json, 'length') !== 1 ||
          (get(json, '[0].sources.length') !== 1 && get(json, '[0].url') === undefined)
        ) {
          throw new Error(' no json data /  bad sources/url');
        }

        let stitched;
        const { sources } = json[0];
        if (sources.length === 1) {
          stitched = {
            urls: [
              {
                type: sources[0].type,
                url: sources[0].file,
              },
            ],
          };
        } else {
          stitched = {
            urls: [
              {
                url: json[0].url,
              },
            ],
          };
        }

        /* we want all channels to have sessionURL, so if sessionURL not found yet,
          sending item to the end of the data array, to set sessionURL later, when it'll exist */
        if (sessionUrl !== null) {
          stitched.sessionURL = sessionUrl;
          res = { ...item, stitched };
        } else {
          dataCopy.push(item);
        }
      } catch (err) {
        log('Err in channel %d: %s', item.number, err.message);
        // note: "res = item" per res declaration
      }
    } else if (sessionUrl === null) {
      sessionUrl = item.stitched.sessionURL;
    }

    return res;
  });
  return Promise.all(promises);
};

export const payloadResolver = (payload) => {
  // Normalize using category _id
  const { result: categories, entities: categoriesEntities } = normalize(
    payload.categories,
    CategoryListSchema,
  );
  // Normalize using channel slug
  const { result: slugs, entities: slugsEntities } = normalize(payload.channels, [
    LinearChannelSlugSchema,
  ]);
  // Normalize using channel number
  const { result: numbers, entities: numbersEntities } = normalize(payload.channels, [
    LinearChannelNumberSchema,
  ]);
  // Normalize using channel _id
  const { result, entities: linearChannelsEntities } = normalize(payload.channels, [
    LinearChannelSchema,
  ]);

  const entities = {
    ...linearChannelsEntities,
    ...numbersEntities,
    ...slugsEntities,
    ...categoriesEntities,
  };

  return {
    result,
    slugs,
    numbers,
    entities,
    categories,
  };
};

const mergeWithOldData = ({ newData, state }) => {
  const { result, entities } = linearChannelsPropertiesSelector(state);
  const oldData = denormalize(result, [LinearChannelSchema], entities);

  const getNewItem = ({ number }) => newData.find((oldItem) => oldItem.number === number);
  const getOldItem = ({ number }) => oldData.find((oldItem) => oldItem.number === number);

  const mergedData = oldData.map((oldItem) => {
    let mergedItem = oldItem;
    const newItem = getNewItem(oldItem);

    if (newItem) {
      const { timelines: oldTimelines = [] } = oldItem;
      const { timelines: newTimelines = [] } = newItem;

      let mergedTimelines = [...oldTimelines, ...newTimelines];

      mergedTimelines = mergedTimelines.reduce((uniqueItems, item) => {
        if (!uniqueItems.find(({ _id }) => _id.toString() === item._id.toString())) {
          uniqueItems.push(item);
        }
        return uniqueItems;
      }, []);

      mergedItem = {
        ...oldItem,
        ...newItem,
        timelines: mergedTimelines.sort((a, b) => (isAfter(a, b) ? -1 : 1)),
      };
    }
    return mergedItem;
  });

  mergedData.push(...newData.filter((newItem) => !getOldItem(newItem)));

  return mergedData;
};

const removeProtocol = (url) => url.replace(/^https?:\/\//g, '//');

const dataEnhancer = (payload, { width, height }) =>
  payload.map((item) => {
    const { id, images } = item;
    const imageBasePath = process.env.CHANNEL_BACKGROUND_IMAGE_BASE_PATH;
    const imagesObj = images.reduce((acc, img) => {
      acc[img.type] = img;
      return acc;
    }, {});
    return {
      ...item,
      _id: id,
      images: imagesObj,
      logo: imagesObj.solidLogoPNG.url,
      background: {
        normal: removeProtocol(`${imageBasePath}/channels/${id}/tile.jpg?w=${width}&h=${height}`),
        grayscale: removeProtocol(
          `${imageBasePath}/channels/${id}/tileGrayscale.jpg?w=${width}&h=${height}`,
        ),
      },
      featuredImage: imagesObj.featuredImage.url,
    };
  });

const filterCategoryByChannelExist = ({ categories, channels }) => {
  const channelsCategoriesID = channels.map((item) => item.categoryID);
  return categories.filter((item) => channelsCategoriesID.includes(item.id));
};

const payloadHandler = ({
  channelBackgroundSize = { width: 210, height: 50 },
  sort: customSort = defaultSort,
  filter: customFilter = defaultFilter,
}) => async (action, state, res) => {
  try {
    const json = await res.json();
    const data = await addUrlForNonStitchedChannels(json.channels, state);
    const filteredData = filterData({ data, customFilter });
    const sortedData = customSort(filteredData);
    const enhancedData = dataEnhancer(sortedData, channelBackgroundSize);

    const payload = mergeWithOldData({ newData: enhancedData, state });
    const categories = filterCategoryByChannelExist({
      categories: json.categories,
      channels: payload,
    });

    return payloadResolver({ channels: payload, categories });
  } catch (err) {
    throw err;
  }
};

const linearChannelsFetchAllApiRequestAction = (
  { start, stop, ...payloadHandlerOptions },
  baseUrl,
) => ({
  [RSAA]: {
    endpoint: `${baseUrl}/v1/guide?start=${encodeURIComponent(
      formatISO(parseISO(start)),
    )}&stop=${encodeURIComponent(formatISO(parseISO(stop)))}`,
    method: 'GET',
    credentials: 'same-origin',
    types: [
      {
        type: FETCH_ALL.REQUEST,
        meta: { params: { start, stop } },
      },
      {
        type: FETCH_ALL.SUCCESS,
        payload: payloadHandler(payloadHandlerOptions),
        meta: { params: { start, stop } },
      },
      {
        type: FETCH_ALL.FAILURE,
        meta: { params: { start, stop } },
      },
    ],
  },
});

export const linearChannelsFetchAllApiThunkAction = (params) => async (dispatch, getState) => {
  const currentState = getState();
  const channelUrl = selectors.application.servers(currentState).channels;
  const { params: oldParams } = linearChannelsPropertiesSelector(currentState);

  if (!oldParams || oldParams.start !== params.start || oldParams.stop !== params.stop) {
    const { payload = {}, error = false } = await dispatch(
      linearChannelsFetchAllApiRequestAction(
        {
          ...params,
          start: params.start,
          stop: params.stop,
        },
        channelUrl,
      ),
    );

    if (
      error &&
      payload.status === 401 &&
      get(payload, 'response.message', '').includes('expired token')
    ) {
      await dispatch(refreshToken({}));
      dispatch(
        linearChannelsFetchAllApiThunkAction({
          ...params,
          tryNumber: params.tryNumber + 1 || 1,
        }),
      );
    }
  }
};
